/* eslint-disable @typescript-eslint/member-ordering */
import { DOCUMENT, inject, Injectable } from '@angular/core';
import { Location } from '@angular/common';
import { EventType, NavigationEnd, NavigationStart, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { SessionStorageService } from '../session-storage';

export interface PersistedRouteHistory {
  history: string[];
  currentIndex: number;
}

export const ROUTE_HISTORY_STORAGE_KEY: string = 'dce.route-history';

@Injectable({
  providedIn: 'root',
})
export class RouteHistoryService {
  private history: string[] = [];
  private currentIndex: number = -1;
  private isPopState: boolean = false;
  private readonly location: Location = inject(Location);
  private readonly router: Router = inject(Router);
  private readonly document: Document = inject(DOCUMENT);
  private readonly sessionStorageService: SessionStorageService = inject(SessionStorageService);

  public constructor() {
    this.restoreHistoryOnReload();
    this.handleBrowserNavigation();
    this.handleAppNavigation();
    this.persistOnUnload();
  }

  /** Detect browser back/forward button presses */
  private handleBrowserNavigation(): void {
    this.router.events
      .pipe(filter(event => event.type === EventType.NavigationStart))
      .subscribe((event: NavigationStart) => {
        this.isPopState = event.navigationTrigger === 'popstate';

        if (this.history.length > 500 && !this.isPopState) {
          this.history = [];
          this.currentIndex = -1;
        }
      });
  }

  /** Track completed navigations */
  private handleAppNavigation(): void {
    this.router.events
      .pipe(filter(event => event.type === EventType.NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        const url: string = event.urlAfterRedirects || event.url;

        if (this.isPopState) {
          // Browser back/forward: move index without rewriting history
          const newIndex: number = this.history.indexOf(url);

          if (newIndex !== -1) {
            this.currentIndex = newIndex;
          }
        } else {
          // Regular navigation: trim forward history and push
          if (this.currentIndex < this.history.length - 1) {
            this.history = this.history.slice(0, this.currentIndex + 1);
          }

          this.history.push(url);
          this.currentIndex++;
        }

        this.isPopState = false;
      });
  }

  public current(): string | undefined {
    return this.history[this.currentIndex] || undefined;
  }

  public back(routeIgnorePattern?: string): void {
    if (this.currentIndex <= 0) {
      void this.router.navigateByUrl('/home');

      return;
    }

    if (!routeIgnorePattern) {
      this.currentIndex--;
      this.location.back();
    } else {
      let historyServiceIndex: number = this.currentIndex--;

      let browserHistoryIndex: number = 1;

      while (historyServiceIndex >= 0 && this.history[historyServiceIndex].includes(routeIgnorePattern)) {
        historyServiceIndex--;
        browserHistoryIndex--;
      }

      this.currentIndex = historyServiceIndex;

      if (historyServiceIndex === 0) {
        void this.router.navigateByUrl('/home');
      } else {
        this.location.historyGo(browserHistoryIndex);
      }
    }
  }

  public forward(): void {
    if (this.currentIndex < this.history.length - 1) {
      this.location.forward();
    }
  }

  /**
   * Restore the in-app history from a previous session so back navigation can "survive" a page reload.
   * The persisted state is only adopted when this load is an actual reload;
   * for any other entry (fresh visit, external link, etc.) the stored state is discarded.
   */
  private restoreHistoryOnReload(): void {
    const stored: PersistedRouteHistory | undefined = this.readPersistedHistory();

    this.sessionStorageService.remove(ROUTE_HISTORY_STORAGE_KEY);

    if (!this.isReload() || !stored) {
      return;
    }

    // The router replays the current URL right after the reload, which re-pushes
    // it onto the history. Drop that entry and step the index back one
    this.history = stored.history.slice(0, stored.currentIndex);
    this.currentIndex = stored.currentIndex - 1;
  }

  private isReload(): boolean {
    const performance: Performance | undefined = this.document.defaultView?.performance;

    if (!performance) {
      return false;
    }

    const [navigation]: PerformanceNavigationTiming[] = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];

    return navigation?.type === 'reload';
  }

  private readPersistedHistory(): PersistedRouteHistory | undefined {
    const raw: string | null = this.sessionStorageService.get(ROUTE_HISTORY_STORAGE_KEY);

    if (!raw) {
      return undefined;
    }

    try {
      const parsed: PersistedRouteHistory = JSON.parse(raw) as PersistedRouteHistory;

      if (!Array.isArray(parsed.history) || typeof parsed.currentIndex !== 'number' || parsed.history.length === 0) {
        return undefined;
      }

      return parsed;
    } catch {
      return undefined;
    }
  }

  private persistOnUnload(): void {
    this.document.defaultView?.addEventListener('beforeunload', () => {
      this.persistHistory();
    });
  }

  private persistHistory(): void {
    if (this.history.length === 0) {
      return;
    }

    const payload: PersistedRouteHistory = {
      history: this.history.slice(),
      currentIndex: this.currentIndex,
    };

    this.sessionStorageService.set(ROUTE_HISTORY_STORAGE_KEY, JSON.stringify(payload));
  }
}
